import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ErrorMessageConstant } from '@utility/constants/common';
import { ComponentsService } from '@utility/services/components';
import ItemService from '@utility/services/item';
import { ToastMessage } from '@utility/toast';
import { cartonKeys } from '../lib/query-keys';
import type { CartonKind } from '../lib/rows';

const showError = (error: { error?: string } | null) =>
  ToastMessage.error(error?.error || ErrorMessageConstant.defaultAPIError);

/* Saves the edited entities, then reloads the list. */
export function useSaveEntities(kind: CartonKind) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (changes: { key: string; entity: string }[]) =>
      kind === 'items'
        ? ItemService.globalItemsUpdate({
            items: changes.map(({ key, entity }) => ({ upc: key, entity })),
          })
        : ComponentsService.globalComponentsUpdate({
            components: changes.map(({ key, entity }) => ({
              _id: key,
              entity,
            })),
          }),
    onSuccess: () => {
      ToastMessage.success('Carton Configuration Updated');
      return queryClient.invalidateQueries({
        queryKey: cartonKeys.lists(kind),
      });
    },
    onError: showError,
  });
}

/* Deletes one carton alias of an item or component, then reloads the list. */
export function useDeleteAlias(kind: CartonKind) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, alias }: { id: string; alias: string }) =>
      kind === 'items'
        ? ItemService.deleteAttribute(id, alias)
        : ComponentsService.deleteAttribute(id, alias),
    onSuccess: () => {
      ToastMessage.success('Item Carton Configuration deleted successfully.');
      return queryClient.invalidateQueries({
        queryKey: cartonKeys.lists(kind),
      });
    },
    onError: showError,
  });
}
